/**
 * ContractReviewDialog — dettaglio contratto + revisione identità (★ Fase 8).
 * L'amministrazione vede documento/selfie + verdetto AI e APPROVA o RIFIUTA.
 * Mittenti (senior) vedono stato, link e PDF dei propri contratti.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Loader2, FileDown, Copy, RefreshCw, Ban, CheckCircle2, XCircle,
  ShieldQuestion, Mail, Eye, Fingerprint, Clock, Paperclip, FileText, Stamp,
  SendHorizonal,
} from 'lucide-react';
import { SafeHtml } from '../../lib/sanitize';
import { STATUS_META, AI_VERDICT_META, fmtDT } from './contractUtils';

function TimelineRow({ done, label, at }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`w-4 h-4 rounded-full grid place-items-center shrink-0 ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
        {done ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
      </span>
      <span className={done ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
      {at && <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">{fmtDT(at)}</span>}
    </div>
  );
}

export function ContractReviewDialog({ contractId, open, onClose, onChanged, canManage }) {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [images, setImages] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [showContent, setShowContent] = useState(false);

  const load = useCallback(async () => {
    if (!contractId) return;
    try {
      const { data } = await api.get(`/contracts/${contractId}`);
      setDoc(data);
      if (canManage && (data.identity || {}).doc_uploaded_at) {
        ['doc', 'doc_back', 'selfie'].forEach((kind) => {
          if (kind === 'doc_back' && !(data.identity || {}).doc_back_uploaded_at) return;
          api.get(`/contracts/${contractId}/files/${kind}`)
            .then((r) => setImages((s) => ({ ...s, [kind]: `data:${r.data.mime};base64,${r.data.content_b64}` })))
            .catch(() => {});
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('reviewContract.loadError', 'Errore caricamento contratto'));
    }
  }, [contractId, canManage]);

  useEffect(() => {
    if (open) { setDoc(null); setImages({}); setNote(''); setShowContent(false); load(); }
  }, [open, load]);

  const act = async (action, body) => {
    setBusy(action);
    try {
      if (action === 'pdf') {
        const r = await api.get(`/contracts/${contractId}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `contratto_${contractId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else if (action === 'approved' || action === 'rejected') {
        await api.post(`/contracts/${contractId}/review`, { decision: action, note });
        toast.success(action === 'approved' ? t('reviewContract.approvedOk', 'Contratto approvato: PDF inviato al cliente.') : t('reviewContract.rejectedOk', 'Contratto rifiutato.'));
        onChanged?.();
        await load();
      } else if (action === 'countersign') {
        const r = await api.post(`/contracts/${contractId}/countersign`);
        toast.success(r.data?.email_sent
          ? t('reviewContract.countersignedSent', 'Controfirmato: PDF a doppia firma inviato al cliente.')
          : t('reviewContract.countersignedOk', 'Contratto controfirmato: PDF a doppia firma disponibile.'));
        onChanged?.();
        await load();
      } else if (action === 'resend') {
        await api.post(`/contracts/${contractId}/resend`);
        toast.success(t('reviewContract.reminderSent', 'Promemoria inviato al firmatario'));
      } else if (action === 'send_again') {
        // ★ Fase 42 — nuovo invio identico (nuovo seriale + nuovo link firma),
        // disponibile da qualsiasi stato: stesso contratto, stessa persona.
        const { data } = await api.post(`/contracts/${contractId}/send-again`);
        const localLink = `${window.location.origin}${data.sign_path}`;
        toast.success(
          t('reviewContract.sendAgainOk', { defaultValue: 'Nuovo invio creato ({{serial}}): email inviata al firmatario.', serial: data.serial_number }),
          {
            action: {
              label: t('sendContract.copyLink', 'Copia link'),
              onClick: () => navigator.clipboard.writeText(localLink).then(() => toast.success(t('sendContract.linkCopied', 'Link copiato'))),
            },
          },
        );
        onChanged?.();
      } else if (action === 'void') {
        await api.post(`/contracts/${contractId}/void`);
        toast.success(t('reviewContract.voidedOk', 'Contratto annullato'));
        onChanged?.();
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('reviewContract.actionFailed', 'Operazione non riuscita'));
    } finally {
      setBusy('');
    }
  };

  const st = doc ? (STATUS_META[doc.status] || {}) : {};
  const ai = doc?.identity?.ai_check;
  const aiMeta = ai ? (AI_VERDICT_META[ai.same_person] || AI_VERDICT_META.non_valutabile) : null;
  const pendingSign = doc && ['sent', 'viewed', 'otp_verified', 'identity_submitted'].includes(doc.status);

  const openAttachment = async (a) => {
    try {
      const r = await api.get(`/contracts/${contractId}/attachments/${a.idx}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: a.mime || 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error(t('reviewContract.attachmentOpenError', 'Impossibile aprire l\u2019allegato'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="contract-review-dialog">
        {!doc ? (
          <div className="space-y-3 py-6">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {doc.template_name}
                <Badge className={`border text-[10px] ${st.cls || ''}`} data-testid="contract-status-badge">{st.label || doc.status}</Badge>
                {doc.serial_number && (
                  <span className="text-[11px] font-mono font-normal text-muted-foreground" data-testid="contract-serial">
                    {doc.serial_number}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                {doc.recipient_name} · {doc.recipient_email} · {t('reviewContract.sentBy', 'inviato da')} {doc.sent_by_name || doc.sent_by}
              </DialogDescription>
            </DialogHeader>

            {/* Timeline */}
            <div className="rounded-lg border border-border bg-[var(--surface-2)]/50 p-3 space-y-1.5" data-testid="contract-timeline">
              <TimelineRow done label={t('reviewContract.tlSent', 'Inviato via email')} at={doc.sent_at} />
              <TimelineRow done={!!doc.viewed_at} label={t('reviewContract.tlViewed', 'Visualizzato dal firmatario')} at={doc.viewed_at} />
              <TimelineRow done={!!doc.otp?.verified_at} label={t('reviewContract.tlOtp', 'Email verificata (OTP)')} at={doc.otp?.verified_at} />
              <TimelineRow done={!!doc.identity?.doc_uploaded_at} label={t('reviewContract.tlIdentity', 'Documento + selfie caricati')} at={doc.identity?.doc_uploaded_at} />
              <TimelineRow done={!!doc.signature?.signed_at} label={t('reviewContract.tlSigned', 'Firmato con consenso esplicito')} at={doc.signature?.signed_at} />
              <TimelineRow
                done={['approved', 'rejected'].includes(doc.status)}
                label={doc.status === 'rejected' ? t('reviewContract.tlRejected', 'Rifiutato in verifica') : t('reviewContract.tlApproved', 'Verifica identità approvata')}
                at={doc.admin_review?.at}
              />
              {/* ★ Fase 14 — controfirma aziendale nella timeline */}
              <TimelineRow
                done={!!doc.countersign?.signed_at}
                label={t('reviewContract.tlCountersign', 'Controfirma aziendale (timbro + firma)')}
                at={doc.countersign?.signed_at}
              />
              {(doc.reminders?.count || 0) > 0 && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1 border-t border-border/60 mt-1" data-testid="contract-reminders-info">
                  <Mail className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    {doc.reminders.count === 1
                      ? t('reviewContract.oneReminder', '1 promemoria automatico inviato')
                      : t('reviewContract.nReminders', { defaultValue: '{{count}} promemoria automatici inviati', count: doc.reminders.count })}
                  </span>
                  {doc.reminders.last_at && (
                    <span className="ml-auto text-[10px] tabular-nums">{t('reviewContract.last', 'ultimo:')} {fmtDT(doc.reminders.last_at)}</span>
                  )}
                </div>
              )}
              {pendingSign && doc.expires_at && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1 border-t border-border/60 mt-1" data-testid="contract-expiry-info">
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                  <span>{t('reviewContract.linkValidUntil', 'Link di firma valido fino al')}</span>
                  <span className="ml-auto text-[10px] tabular-nums">{fmtDT(doc.expires_at)}</span>
                </div>
              )}
              {doc.status === 'expired' && (
                <div className="flex items-center gap-2 text-xs text-orange-700 pt-1 border-t border-border/60 mt-1" data-testid="contract-expired-info">
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                  <span>{t('reviewContract.linkExpired', 'Link scaduto per sicurezza (30 giorni): invia un nuovo contratto se necessario.')}</span>
                </div>
              )}
            </div>

            {/* Identità (solo amministrazione) */}
            {/* ★ Fase 8.T — Allegati inviati col contratto */}
            {(doc.attachments || []).length > 0 && (
              <div className="rounded-lg border border-border p-3 space-y-1.5" data-testid="contract-attachments">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Paperclip className="w-4 h-4 text-[hsl(var(--primary))]" /> {t('reviewContract.attachments', 'Allegati')} ({doc.attachments.length})
                </div>
                {doc.attachments.map((a) => (
                  <button
                    key={a.idx}
                    type="button"
                    className="w-full flex items-center gap-2 rounded-md border border-border hover:bg-[var(--surface-2)]/60 px-2.5 py-1.5 text-left"
                    onClick={() => openAttachment(a)}
                    data-testid={`contract-attachment-${a.idx}`}
                  >
                    <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs truncate flex-1">{a.name}</span>
                    <Eye className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            )}

            {canManage && doc.identity?.doc_uploaded_at && (
              <div className="rounded-lg border border-border p-3 space-y-3" data-testid="contract-identity-review">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Fingerprint className="w-4 h-4 text-[hsl(var(--primary))]" /> {t('reviewContract.identityCheck', 'Verifica identità')}
                  {aiMeta && (
                    <span className={`ml-auto text-xs font-semibold inline-flex items-center gap-1 ${aiMeta.cls}`} data-testid="contract-ai-verdict">
                      <ShieldQuestion className="w-3.5 h-3.5" /> AI: {aiMeta.label}
                    </span>
                  )}
                </div>
                {ai?.notes && <p className="text-xs text-muted-foreground italic">“{ai.notes}” {ai.document_type ? `· ${t('reviewContract.document', 'Documento')}: ${ai.document_type}` : ''}</p>}
                {doc.identity?.doc_type && (
                  <div className="flex flex-wrap gap-2 text-xs" data-testid="contract-identity-meta">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5" data-testid="contract-doc-type">
                      {doc.identity.doc_type === 'passport' ? t('reviewContract.docPassport', 'Passaporto') : t('reviewContract.docIdCard', 'Carta d\u2019identit\u00e0 (fronte + retro)')}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 ${ai?.document_in_selfie ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
                      data-testid="contract-doc-in-selfie"
                    >
                      {t('reviewContract.docInSelfie', 'Documento in mano nel selfie')}: {ai?.document_in_selfie ? t('reviewContract.yes', 's\u00ec') : t('reviewContract.toCheck', 'da verificare')}
                    </span>
                  </div>
                )}
                <div className={`grid gap-3 ${doc.identity?.doc_back_uploaded_at ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  {['doc', 'doc_back', 'selfie'].filter((k) => k !== 'doc_back' || doc.identity?.doc_back_uploaded_at).map((kind) => (
                    <div key={kind}>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                        {kind === 'doc' ? t('reviewContract.idDocument', 'Documento d\u2019identit\u00e0') : kind === 'doc_back' ? t('reviewContract.docBack', 'Retro documento') : 'Selfie'}
                      </div>
                      {images[kind] ? (
                        <img src={images[kind]} alt={kind} className="rounded-md border border-border max-h-52 w-full object-contain bg-slate-50" data-testid={`contract-image-${kind}`} />
                      ) : (
                        <Skeleton className="h-40 w-full" />
                      )}
                    </div>
                  ))}
                </div>

                {doc.status === 'signed' && (
                  <div className="space-y-2 pt-1">
                    <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('reviewContract.reviewNotePlaceholder', 'Nota di revisione (obbligatoria se rifiuti)…')} data-testid="contract-review-note" />
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        disabled={!!busy || !note.trim()}
                        onClick={() => act('rejected')}
                        data-testid="contract-reject-btn"
                      >
                        {busy === 'rejected' ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <XCircle className="w-4 h-4 mr-1.5" />}
                        {t('reviewContract.reject', 'Rifiuta')}
                      </Button>
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={!!busy}
                        onClick={() => act('approved')}
                        data-testid="contract-approve-btn"
                      >
                        {busy === 'approved' ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                        {t('reviewContract.approveAndSend', 'Approva e invia PDF al cliente')}
                      </Button>
                    </div>
                  </div>
                )}
                {doc.admin_review?.note && (
                  <p className="text-xs text-muted-foreground">{t('reviewContract.reviewNote', 'Nota revisione:')} “{doc.admin_review.note}” — {doc.admin_review.by}</p>
                )}
              </div>
            )}

            {/* ★ Fase 14 — Controfirma aziendale (solo amministrazione) */}
            {canManage && ['signed', 'approved'].includes(doc.status) && (
              <div className="rounded-lg border border-border p-3 space-y-2" data-testid="contract-countersign-block">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Stamp className="w-4 h-4 text-[hsl(var(--primary))]" /> {t('reviewContract.countersignTitle', 'Controfirma aziendale')}
                </div>
                {doc.countersign?.signed_at ? (
                  <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-2" data-testid="contract-countersigned-info">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span>
                      {t('reviewContract.countersignedBy', 'Controfirmato con timbro da')} <b>{doc.countersign.signer_name}</b> {t('reviewContract.onDate', 'il')} {fmtDT(doc.countersign.signed_at)}.
                      {' '}{t('reviewContract.clientReceivedPdf', 'Il cliente ha ricevuto il PDF definitivo a doppia firma.')}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-muted-foreground flex-1 min-w-[220px]">
                      {t('reviewContract.countersignDesc', 'Apponi timbro House Cash Flows e firma di Richard Demichele in un click: il PDF a doppia firma viene rigenerato e inviato automaticamente al cliente.')}
                    </p>
                    <Button
                      className="bg-[#1a3dae] hover:bg-[#16348f] text-white"
                      disabled={!!busy}
                      onClick={() => act('countersign')}
                      data-testid="contract-countersign-btn"
                    >
                      {busy === 'countersign' ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Stamp className="w-4 h-4 mr-1.5" />}
                      {t('reviewContract.countersignBtn', 'Controfirma azienda')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Azioni */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowContent((s) => !s)} data-testid="contract-show-content">
                <Eye className="w-3.5 h-3.5 mr-1" /> {showContent ? t('reviewContract.hideContract', 'Nascondi contratto') : t('reviewContract.showContract', 'Vedi contratto')}
              </Button>
              <Button
                size="sm" variant="outline" className="h-8 text-xs"
                onClick={() => navigator.clipboard.writeText(`${window.location.origin}${doc.sign_path}`).then(() => toast.success(t('sendContract.signLinkCopied', 'Link firma copiato')))}
                data-testid="contract-copy-link"
              >
                <Copy className="w-3.5 h-3.5 mr-1" /> {t('sendContract.copySignLink', 'Copia link firma')}
              </Button>
              {pendingSign && (
                <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!!busy} onClick={() => act('resend')} data-testid="contract-resend-btn">
                  {busy === 'resend' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />} {t('reviewContract.resendEmail', 'Reinvia email')}
                </Button>
              )}
              {/* ★ Fase 42 — Invia di nuovo: nuovo contratto identico (nuovo link), da qualsiasi stato */}
              <Button
                size="sm" variant="outline"
                className="h-8 text-xs border-[var(--gp-gold)]/60 text-[hsl(var(--primary))] hover:bg-[var(--gp-gold)]/10"
                disabled={!!busy}
                onClick={() => {
                  if (window.confirm(t('reviewContract.sendAgainConfirm', 'Inviare di nuovo questo contratto allo stesso destinatario? Verrà creato un nuovo invio con un nuovo link di firma.'))) {
                    act('send_again');
                  }
                }}
                data-testid="contract-send-again-btn"
              >
                {busy === 'send_again' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <SendHorizonal className="w-3.5 h-3.5 mr-1" />} {t('reviewContract.sendAgain', 'Invia di nuovo')}
              </Button>
              {pendingSign && (
                <Button size="sm" variant="outline" className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50" disabled={!!busy} onClick={() => act('void')} data-testid="contract-void-btn">
                  {busy === 'void' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Ban className="w-3.5 h-3.5 mr-1" />} {t('reviewContract.voidBtn', 'Annulla')}
                </Button>
              )}
              {['signed', 'approved', 'rejected'].includes(doc.status) && (
                <Button size="sm" className="h-8 text-xs ml-auto" disabled={!!busy} onClick={() => act('pdf')} data-testid="contract-pdf-btn">
                  {busy === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileDown className="w-3.5 h-3.5 mr-1" />} {t('reviewContract.downloadSignedPdf', 'Scarica PDF firmato')}
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={load} title={t('reviewContract.reload', 'Ricarica')}>
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>

            {showContent && (
              <div className="rounded-lg border border-border bg-white p-4 max-h-[300px] overflow-y-auto prose prose-sm max-w-none" data-testid="contract-content-preview">
                <SafeHtml html={doc.content_html_final} />
              </div>
            )}

            <p className="text-[10px] text-muted-foreground">
              Hash SHA-256: <code className="break-all">{doc.document_hash}</code>
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
